import React, { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import {
  Box,
  Button,
  Dialog,
  DialogContent,
  IconButton,
  LinearProgress,
  Paper,
  Typography,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import RadioButtonUncheckedIcon from "@mui/icons-material/RadioButtonUnchecked";
import PlayCircleOutlineIcon from "@mui/icons-material/PlayCircleOutline";
import { apiClient } from "../../services/authService";
import { getClientsPage } from "../../services/ClientsService";
import { listConsentForms } from "../../services/clientRecordsService";
import { useSubscription } from "../../contexts/SubscriptionContext";
import { useUser } from "../../contexts/UserContext";
import { BRAND, LABELS } from "../../platform/config";
import { SETUP_TOPICS, TOUR_TOPICS, TourStep, TourTopic } from "./tourTopics";

type TourContextType = {
  /** Opens the help center (the setup checklist plus every how-to). */
  openHelp: () => void;
  startTour: (topicId: string) => void;
};

const TourContext = createContext<TourContextType>({ openHelp: () => undefined, startTour: () => undefined });

export const useTour = () => useContext(TourContext);

const Z = 1400; // above drawers and dialogs, below toasts

const readList = (key: string): string[] => {
  try {
    const value = JSON.parse(localStorage.getItem(key) || "[]");
    return Array.isArray(value) ? value : [];
  } catch {
    return [];
  }
};

/** Finds a `data-tour` element that is actually on screen. */
const findTarget = (target?: string): HTMLElement | null => {
  if (!target) return null;
  const all = Array.from(document.querySelectorAll<HTMLElement>(`[data-tour="${target}"]`));
  return all.find((el) => {
    const r = el.getBoundingClientRect();
    return r.width > 0 && r.height > 0;
  }) ?? null;
};

/**
 * Help and the first-run setup guide for a backoffice. Wraps a layout:
 * - the help center lists how to do the important things; each "Show me"
 *   opens the right page and points at the real button with a spotlight;
 * - a new owner on a trial sees the setup checklist once, the first time.
 */
export const TourProvider: React.FC<{ base: string; children: React.ReactNode }> = ({ base, children }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const theme = useTheme();
  const isPhone = useMediaQuery(theme.breakpoints.down("sm"));
  const { user } = useUser();
  const { subscription } = useSubscription();
  const orgId = (user as any)?.organizationId as string | undefined;
  const businessId = base.split("/").pop() || "";
  const doneKey = `tour-done:${orgId}`;
  const seenKey = `setup-guide-seen:${orgId}`;

  const [helpOpen, setHelpOpen] = useState(false);
  const [firstRun, setFirstRun] = useState(false);
  const [done, setDone] = useState<string[]>([]);
  const [checking, setChecking] = useState(false);
  const [run, setRun] = useState<{ topic: TourTopic; index: number } | null>(null);
  const [rect, setRect] = useState<DOMRect | null>(null);
  // The step whose button has been found (or given up on): only then is it drawn.
  const [readyStep, setReadyStep] = useState<TourStep | null>(null);
  // The page's own pop-up (opened from the highlighted button) is on screen.
  const [paused, setPaused] = useState(false);
  const targetRef = useRef<HTMLElement | null>(null);
  const arrivedRef = useRef<TourStep | null>(null);

  useEffect(() => {
    if (orgId) setDone(readList(doneKey));
  }, [orgId, doneKey]);

  // What is already set up, read from the organization's data; topics with
  // nothing to count (opening hours, a first booking) are done once shown.
  const checkProgress = useCallback(async () => {
    if (!orgId) return;
    setChecking(true);
    const found = new Set(readList(doneKey));
    const has = async (id: string, probe: () => Promise<boolean>) => {
      try {
        if (await probe()) found.add(id);
      } catch {
        // No permission or offline: the local mark decides.
      }
    };
    await Promise.all([
      has("units", async () => {
        const { data } = await apiClient.get<any>("/units/all");
        return (Array.isArray(data) ? data : data?.data ?? []).length > 0;
      }),
      has("services", async () => {
        const { data } = await apiClient.get<any>(`/services/business/${businessId}`);
        const list: any[] = Array.isArray(data) ? data : data?.data ?? [];
        // The default "1 hour" service every new calendar gets does not count.
        return list.some((s) => s?.active !== false && s?.name !== "1 hour");
      }),
      has("consent", async () => (await listConsentForms()).length > 0),
      has("clients", async () => {
        // The list also holds the signed-in owner, who is not a first client.
        const page = await getClientsPage({ businessId: "all", page: 0, size: 5 } as any);
        const me = (user as any)?.id ?? (user as any)?.organizationUserId;
        return (page?.content ?? []).some((c: any) => c?.id !== me) || (page?.totalElements ?? 0) > 5;
      }),
    ]);
    setDone(Array.from(found));
    setChecking(false);
  }, [orgId, doneKey, businessId, user]);

  const openHelp = useCallback(() => {
    setRun(null);
    setRect(null);
    setFirstRun(false);
    setHelpOpen(true);
    checkProgress();
  }, [checkProgress]);

  // First time on a trial: the setup checklist, once.
  useEffect(() => {
    if (!orgId || !subscription?.isTrial || localStorage.getItem(seenKey)) return;
    localStorage.setItem(seenKey, "1");
    setFirstRun(true);
    setHelpOpen(true);
    checkProgress();
  }, [orgId, subscription?.isTrial, seenKey, checkProgress]);

  const markDone = useCallback(
    (id: string) => {
      const next = Array.from(new Set([...readList(doneKey), id]));
      localStorage.setItem(doneKey, JSON.stringify(next));
      setDone(next);
    },
    [doneKey]
  );

  const startTour = useCallback(
    (topicId: string) => {
      const topic = TOUR_TOPICS.find((t) => t.id === topicId);
      if (!topic) return;
      setHelpOpen(false);
      setRun({ topic, index: 0 });
    },
    []
  );

  const step: TourStep | null = run ? run.topic.steps[run.index] : null;
  const isLast = run ? run.index === run.topic.steps.length - 1 : false;

  const finish = useCallback(() => {
    if (run) markDone(run.topic.id);
    setRun(null);
    setRect(null);
  }, [run, markDone]);

  const next = useCallback(() => {
    if (!run) return;
    if (isLast) finish();
    else setRun({ ...run, index: run.index + 1 });
  }, [run, isLast, finish]);

  const onStepPage =
    !step ||
    step.path === undefined ||
    location.pathname.replace(/\/$/, "") === `${base}${step.path}`.replace(/\/$/, "");

  // Open the step's page, once per step.
  useEffect(() => {
    if (step && !onStepPage) navigate(`${base}${step.path}`);
  }, [step]); // eslint-disable-line react-hooks/exhaustive-deps

  // Hide the tour while a pop-up is open (usually the one the highlighted
  // button opened), and bring the same step back once it closes. Steps only
  // move on with Next, so each one waits for the owner.
  useEffect(() => {
    if (!run) {
      setPaused(false);
      return;
    }
    const check = () => setPaused(Boolean(document.querySelector(".MuiModal-root:not(.MuiModal-hidden)")));
    check();
    const timer = window.setInterval(check, 250);
    return () => window.clearInterval(timer);
  }, [run]);

  // Wait for the step's button to appear on its page. Nothing is drawn until
  // the spotlight is in place, so no card flashes up in the middle first.
  useEffect(() => {
    if (!step) return;
    setReadyStep(null);
    if (!onStepPage) {
      // They followed the button to another page: the tour has done its job.
      if (arrivedRef.current === step) finish();
      return;
    }
    arrivedRef.current = step;
    setRect(null);
    targetRef.current = null;
    if (paused) return;
    if (!step.target) {
      setReadyStep(step);
      return;
    }
    let tries = 0;
    let settle = 0;
    const timer = window.setInterval(() => {
      const el = findTarget(step.target);
      tries += 1;
      if (el || tries > 40) {
        window.clearInterval(timer);
        targetRef.current = el;
        if (!el) {
          setReadyStep(step); // not on screen: the card alone, in the middle
          return;
        }
        el.scrollIntoView({ block: "center", behavior: "smooth" });
        settle = window.setTimeout(() => {
          setRect(el.getBoundingClientRect());
          setReadyStep(step);
        }, 350);
      }
    }, 150);
    return () => {
      window.clearInterval(timer);
      window.clearTimeout(settle);
    };
  }, [step, onStepPage, paused, finish]);

  // Follow the button while the page scrolls or resizes.
  useEffect(() => {
    const el = targetRef.current;
    if (!step || !el) return;
    const update = () => setRect(el.getBoundingClientRect());
    window.addEventListener("resize", update);
    window.addEventListener("scroll", update, true);
    return () => {
      window.removeEventListener("resize", update);
      window.removeEventListener("scroll", update, true);
    };
  }, [step, rect === null]); // eslint-disable-line react-hooks/exhaustive-deps

  // Closing the welcome checklist shows where Help lives, so it can be found again.
  const closeHelp = () => {
    setHelpOpen(false);
    if (firstRun) {
      setFirstRun(false);
      startTour("help");
    }
  };

  const setupDone = SETUP_TOPICS.filter((t) => done.includes(t.id)).length;
  const value = useMemo(() => ({ openHelp, startTour }), [openHelp, startTour]);

  // ----- the spotlight and its card ------------------------------------

  const pad = 6;
  const cardWidth = 340;
  let cardStyle: React.CSSProperties;
  if (isPhone || !rect) {
    cardStyle = isPhone
      ? { left: 12, right: 12, bottom: 16 }
      : { left: "50%", top: "50%", transform: "translate(-50%, -50%)", width: cardWidth };
  } else {
    const below = rect.bottom + pad + 12;
    const fitsBelow = below + 200 < window.innerHeight;
    const left = Math.min(Math.max(12, rect.left), window.innerWidth - cardWidth - 12);
    cardStyle = fitsBelow
      ? { top: below, left, width: cardWidth }
      : { bottom: window.innerHeight - rect.top + pad + 12, left, width: cardWidth };
  }

  const overlay = run && step && !paused && readyStep === step && (
    <>
      {rect ? (
        <Box
          aria-hidden
          sx={{
            position: "fixed",
            zIndex: Z,
            top: rect.top - pad,
            left: rect.left - pad,
            width: rect.width + pad * 2,
            height: rect.height + pad * 2,
            borderRadius: "10px",
            boxShadow: "0 0 0 9999px rgba(15, 23, 42, 0.55)",
            outline: "3px solid #38BDF8",
            pointerEvents: "none",
            transition: "all 200ms ease",
          }}
        />
      ) : (
        <Box aria-hidden sx={{ position: "fixed", inset: 0, zIndex: Z, bgcolor: "rgba(15, 23, 42, 0.55)" }} />
      )}
      <Paper
        role="dialog"
        aria-label={step.title}
        elevation={8}
        style={cardStyle}
        sx={{ position: "fixed", zIndex: Z + 1, borderRadius: 3, p: 2 }}
      >
        <Box sx={{ display: "flex", alignItems: "flex-start", gap: 1 }}>
          <Box sx={{ flexGrow: 1 }}>
            {run.topic.steps.length > 1 && (
              <Typography sx={{ fontSize: "0.72rem", fontWeight: 700, color: "primary.main", letterSpacing: 0.5 }}>
                STEP {run.index + 1} OF {run.topic.steps.length}
              </Typography>
            )}
            <Typography sx={{ fontWeight: 700 }}>{step.title}</Typography>
          </Box>
          <IconButton size="small" aria-label="End the tour" onClick={finish} sx={{ mt: -0.5, mr: -0.5 }}>
            <CloseIcon fontSize="small" />
          </IconButton>
        </Box>
        <Typography sx={{ fontSize: "0.9rem", color: "text.secondary", mt: 0.75, lineHeight: 1.55 }}>
          {step.body}
        </Typography>
        <Box sx={{ display: "flex", gap: 1, mt: 1.75 }}>
          <Button size="small" onClick={() => { finish(); openHelp(); }} sx={{ textTransform: "none", mr: "auto" }}>
            All topics
          </Button>
          {run.index > 0 && (
            <Button size="small" onClick={() => setRun({ ...run, index: run.index - 1 })} sx={{ textTransform: "none" }}>
              Back
            </Button>
          )}
          <Button size="small" variant="contained" disableElevation onClick={next} sx={{ textTransform: "none" }}>
            {isLast ? "Got it" : "Next"}
          </Button>
        </Box>
      </Paper>
    </>
  );

  // ----- the help center / setup checklist -----------------------------

  const topicRow = (topic: TourTopic, index?: number) => {
    const isDone = done.includes(topic.id);
    return (
      <Box
        key={topic.id}
        sx={{ display: "flex", alignItems: "center", gap: 1.5, py: 1.25, borderTop: "1px solid", borderColor: "divider" }}
      >
        {index !== undefined ? (
          isDone ? (
            <CheckCircleIcon sx={{ color: "#16A34A" }} aria-label="Done" />
          ) : (
            <RadioButtonUncheckedIcon sx={{ color: "text.disabled" }} aria-label="Not done yet" />
          )
        ) : (
          <PlayCircleOutlineIcon sx={{ color: "primary.main" }} aria-hidden />
        )}
        <Box sx={{ flexGrow: 1, minWidth: 0 }}>
          <Typography sx={{ fontWeight: 600, textDecoration: index !== undefined && isDone ? "line-through" : "none", color: index !== undefined && isDone ? "text.secondary" : "text.primary" }}>
            {topic.title}
          </Typography>
          <Typography sx={{ fontSize: "0.82rem", color: "text.secondary" }}>{topic.summary}</Typography>
        </Box>
        <Button size="small" variant={index !== undefined && !isDone ? "contained" : "outlined"} disableElevation onClick={() => startTour(topic.id)} sx={{ textTransform: "none", whiteSpace: "nowrap" }}>
          Show me
        </Button>
      </Box>
    );
  };

  return (
    <TourContext.Provider value={value}>
      {children}
      {overlay}
      <Dialog open={helpOpen} onClose={closeHelp} fullScreen={isPhone} fullWidth maxWidth="sm">
        <DialogContent sx={{ p: { xs: 2.5, sm: 3 } }}>
          <Box sx={{ display: "flex", alignItems: "flex-start", gap: 1, mb: 1 }}>
            <Box sx={{ flexGrow: 1 }}>
              <Typography sx={{ fontWeight: 800, fontSize: "1.3rem" }}>
                {firstRun ? `Welcome to ${BRAND.name}` : "Help"}
              </Typography>
              <Typography sx={{ color: "text.secondary", fontSize: "0.92rem" }}>
                {firstRun
                  ? `A few steps to get your ${LABELS.venue.toLowerCase()} ready. Each "Show me" takes you to the right page and points at the button.`
                  : "Pick a topic: we open the page and show you the button."}
              </Typography>
            </Box>
            <IconButton aria-label="Close" onClick={closeHelp}>
              <CloseIcon />
            </IconButton>
          </Box>

          <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, mt: 2, mb: 1 }}>
            <Typography sx={{ fontWeight: 700, flexGrow: 1 }}>Set up your account</Typography>
            <Typography sx={{ fontSize: "0.85rem", color: "text.secondary" }}>
              {setupDone} of {SETUP_TOPICS.length} done
            </Typography>
          </Box>
          <LinearProgress
            variant={checking ? "indeterminate" : "determinate"}
            value={(setupDone / SETUP_TOPICS.length) * 100}
            sx={{ height: 6, borderRadius: 3, mb: 1 }}
          />
          {SETUP_TOPICS.map((topic, i) => topicRow(topic, i))}

          <Typography sx={{ fontWeight: 700, mt: 3, mb: 1 }}>More</Typography>
          {TOUR_TOPICS.filter((t) => !t.setup).map((topic) => topicRow(topic))}

          {firstRun && (
            <Box sx={{ display: "flex", justifyContent: "flex-end", mt: 2 }}>
              <Button onClick={closeHelp} sx={{ textTransform: "none" }}>
                I'll do it later
              </Button>
            </Box>
          )}
        </DialogContent>
      </Dialog>
    </TourContext.Provider>
  );
};

export default TourProvider;
